import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { request } from '@/lib/http';
import { ApiError } from '@/features/auth/api';

/** Structured postal address, mirroring the API's `CustomerAddress*` DTOs. */
export interface CustomerAddress {
  locality: string;
  street: string | null;
  buildingNumber: string;
  unitNumber: string | null;
  postalCode: string;
  postTown: string;
  countryCode: string;
}

/** A company resolved from the GUS REGON registry (`GET /customers/search`). */
export interface Company {
  name: string;
  nip: string;
  regon: string | null;
  address: CustomerAddress | null;
  addressLines: string[];
}

/** Whether a customer is a company (identified by NIP) or a private person (no NIP). */
export type CustomerType = 'COMPANY' | 'PRIVATE_PERSON';

/** A saved customer (buyer), mirroring the API's `CustomerResponse`. */
export interface Customer {
  id: number;
  type: CustomerType;
  nip: string | null;
  name: string;
  firstName: string | null;
  lastName: string | null;
  regon: string | null;
  address: CustomerAddress;
  email: string | null;
  phone: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Create/replace body, mirroring the API's `CustomerRequest` (no id, ownership, or timestamps). */
export interface CustomerInput {
  type: CustomerType;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  nip: string | null;
  regon: string | null;
  address: CustomerAddress;
  email: string | null;
  phone: string | null;
}

interface CustomerPage {
  items: Customer[];
  nextOffset: number | null;
}

export const CUSTOMERS_QUERY_KEY = ['customers'] as const;

/** Page size for the customer list — matches the API default. */
export const CUSTOMERS_PAGE_SIZE = 50;

async function fetchCustomerPage(query: string, offset: number): Promise<CustomerPage> {
  const params = new URLSearchParams({
    limit: String(CUSTOMERS_PAGE_SIZE),
    offset: String(offset),
  });
  const q = query.trim();
  if (q !== '') params.set('q', q);
  return request<CustomerPage>(`/api/v1/customers?${params.toString()}`);
}

/**
 * Saved customers, server-searched by [query] (name/address substrings + digits-only nip/regon) and
 * fetched a page at a time as the list scrolls. Call `fetchNextPage` to pull more; flatten `data.pages`
 * to render. Changing [query] starts a fresh query (its trimmed value is part of the key).
 */
export function useCustomers(query: string) {
  return useInfiniteQuery({
    queryKey: [...CUSTOMERS_QUERY_KEY, 'list', query.trim()],
    queryFn: ({ pageParam }) => fetchCustomerPage(query, pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * A single saved customer by id (`GET /customers/{id}`), disabled while [id] is null. Shares the
 * `['customers']` prefix, so any create/update/delete mutation invalidates it too — an inline edit
 * of the buyer refreshes its preview automatically.
 */
export function useCustomer(id: number | null) {
  return useQuery<Customer>({
    queryKey: [...CUSTOMERS_QUERY_KEY, 'detail', id],
    queryFn: () => request<Customer>(`/api/v1/customers/${id}`),
    enabled: id != null,
    staleTime: 30_000,
  });
}

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation<Customer, ApiError, CustomerInput>({
    mutationFn: (input) =>
      request<Customer>('/api/v1/customers', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: CUSTOMERS_QUERY_KEY }),
  });
}

export function useUpdateCustomer() {
  const qc = useQueryClient();
  return useMutation<Customer, ApiError, { id: number; input: CustomerInput }>({
    mutationFn: ({ id, input }) =>
      request<Customer>(`/api/v1/customers/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: CUSTOMERS_QUERY_KEY }),
  });
}

export function useDeleteCustomer() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, number>({
    mutationFn: (id) => request<void>(`/api/v1/customers/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: CUSTOMERS_QUERY_KEY }),
  });
}

export function useCompanyLookup() {
  return useMutation<Company, ApiError, string>({
    mutationFn: (nip: string) =>
      request<Company>(`/api/v1/customers/search?nip=${encodeURIComponent(nip)}`),
  });
}
